import React, { useEffect, useMemo, useState } from 'react'
import { withTranslation } from 'react-i18next'
import useSWR, { mutate } from 'swr'
import dayjs from 'dayjs'
import * as UIcons from '@iconscout/react-unicons'
import { toast } from 'react-toastify'
import { apiRequest } from 'store/Misc/apiRequest'
import usePermissions from 'hooks/usePermissions'
import { withRouter } from 'hooks/withRouter'
import CampaignStats from './components/CampaignStats'
import CampaignFilterBar from './components/CampaignFilterBar'
import CampaignsTable from './components/CampaignsTable'
import CampaignsPagination from './components/CampaignsPagination'
import PreviewCampaign from './components/PromoCardDetails/components/Campaigns/PreviewCampaign'

const CAMPAIGNS_KEY = '/promos/quiz-games?status=ongoing'

export default withTranslation()(withRouter(function Promo({ t, router: { navigate } }) {
  const { isPermitted } = usePermissions()
  const { data, isValidating } = useSWR(CAMPAIGNS_KEY)

  const [search, setSearch] = useState('')
  const [period, setPeriod] = useState({ from: '', to: '' })
  const [status, setStatus] = useState('')
  const [budget, setBudget] = useState({ min: 0, max: 0 })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [selected, setSelected] = useState({})
  const [preview, setPreview] = useState(null)

  const campaigns = useMemo(() => (data?.data ?? []).map((quizGame) => ({
    id: quizGame.id,
    title: quizGame.name,
    status: quizGame.status,
    thumbnail_stream: quizGame.image,
    start: quizGame.startDate,
    end: quizGame.endDate,
    type: quizGame.category,
    budget: Number(String(quizGame.price).replace(/[^0-9.]/g, '')) || 0,
    participants_count: quizGame.totalPlayers,
    performance_multiplier: quizGame.percentageToPoints
  })), [data])

  const filtered = useMemo(() => campaigns.filter((campaign) => {
    if (search && !campaign.title?.toLowerCase().includes(search.toLowerCase())) return false
    if (status && (campaign.status ?? '').toLowerCase() !== status) return false
    if (period.from && campaign.end && dayjs(campaign.end).isBefore(dayjs(period.from))) return false
    if (period.to && campaign.start && dayjs(campaign.start).isAfter(dayjs(period.to))) return false
    if (budget.min && campaign.budget < budget.min) return false
    if (budget.max && campaign.budget > budget.max) return false
    return true
  }), [campaigns, search, status, period, budget])

  useEffect(() => { setPage(1) }, [search, status, period, budget, pageSize])

  const lastPage = Math.max(1, Math.ceil(filtered.length / pageSize))
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize)
  const selectedCount = Object.values(selected).filter(Boolean).length

  const toggleSelect = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }))

  const togglePause = async (campaign) => {
    try {
      await apiRequest({ method: 'get', url: `promos/campaigns/${campaign.id}/pause` })
      mutate(CAMPAIGNS_KEY)
    } catch (error) {
      toast.error(error?.response?.data?.message ?? 'Unable to update campaign')
    }
  }

  const deleteCampaign = async (campaign) => {
    try {
      await apiRequest({ method: 'delete', url: `promos/campaigns/${campaign.id}` })
      toast.success('Campaign deleted successfully')
      mutate(CAMPAIGNS_KEY)
    } catch (error) {
      toast.error(error?.response?.data?.message ?? 'Unable to delete campaign')
    }
  }

  return (
    <section className="w-full max-w-6xl mx-auto pb-10">
      <div className="flex items-center justify-between gap-4 mb-5">
        <h1 className="text-2xl font-semibold text-dark-blue">
          {t('promo.campaigns')} <span className="text-secondary opacity-70">({campaigns.length})</span>
        </h1>

        {
          isPermitted?.create_campaign && (
            <button
              type="button"
              onClick={() => navigate('/promo/campaign/create-campaign')}
              className="flex items-center gap-1.5 bg-primary text-white rounded-full h-10 px-4 text-sm font-medium whitespace-nowrap"
            >
              <UIcons.UilPlus size={18} /> {t('promo.add')}
            </button>
          )
        }
      </div>

      <CampaignStats campaigns={campaigns} />

      <div className="bg-white rounded-2xl border border-border-lines px-4">
        <CampaignFilterBar
          search={search}
          onChangeSearch={setSearch}
          period={period}
          onChangePeriod={setPeriod}
          status={status}
          onChangeStatus={setStatus}
          budget={budget}
          onChangeBudget={setBudget}
        />

        <CampaignsTable
          campaigns={paginated}
          isLoading={isValidating}
          selected={selected}
          onToggleSelect={toggleSelect}
          onViewDetails={(campaign) => navigate(`/promo/campaigns/${campaign.id}`)}
          onPreview={setPreview}
          onTogglePause={togglePause}
          onDelete={deleteCampaign}
          canPause={isPermitted?.create_campaign}
          canDelete={isPermitted?.delete_campaign}
        />

        <CampaignsPagination
          total={filtered.length}
          currentPage={page}
          lastPage={lastPage}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          selectedCount={selectedCount}
        />
      </div>

      {preview && <PreviewCampaign promo={preview} close={() => setPreview(null)} />}
    </section>
  )
}))
